import { useCallback, useRef } from 'react';
import { fetchApi } from 'react-scale/redux/actions';
import axios from 'axios';
import pMap from 'p-map';
import { sleep } from '@scale/shared/utils';
import { logEvent } from '@scale/frontend-lib/events';
import { Event } from '@scale/shared/types/events/index';
import { SupportedAudioFormat } from '@scale/mm-shared';

const CDS_CHUNK_SIZE = 20 * 1024 * 1024; // 20 MB

// Types of files that support automatic conversions from different types.
type ConversionSupportedFileTypes = SupportedAudioFormat;
interface UploadPartsParams {
  file: File;
  // If a file type is specified, the server will attempt to convert the file to that type.
  fileType?: ConversionSupportedFileTypes;
  partUrls: string[];
  bodyParts: Blob[];
  uploadId: string;
  itemId: string;
  cdsUrl: string;
  signedCdsUrl: string;
  onProgress?: (progress: number) => void;
  onError?: (message: string) => void;
}
interface CDSMultipartUploadHook {
  upload: ({ file, onProgress, onComplete, onError, fileType }: UploadInput) => Promise<{
    cdsUrl: string;
    s3Url: string;
    signedCdsUrl: string;
  }>;

  retryUpload: ({ file, onProgress, onComplete, onError, fileType }: UploadInput) => Promise<{
    cdsUrl: string;
    s3Url: string;
    signedCdsUrl: string;
  }>;
}

type UploadInput = {
  file: File;
  onProgress?: (progress: number) => void;
  onComplete?: (cdsUrl: string, signedCdsUrl: string) => void;
  onError?: (message: string) => void;
  fileType?: ConversionSupportedFileTypes;
};

const useCDSMultipartUpload = (
  assignmentId?: string,
  workerId?: string,
): CDSMultipartUploadHook => {
  // These refs are used to store data necessary for resuming an upload in the case of a failure
  const cdsUrlRef = useRef<string | null>(null);
  const apiS3UrlRef = useRef<string | null>(null);
  const signedCdsUrlRef = useRef<string | null>(null);
  const partUrlsRef = useRef<string[] | null>(null);
  const uploadIdRef = useRef<string | null>(null);
  const itemIdRef = useRef<string | null>(null);
  const completedPartsRef = useRef<Record<number, { eTag: string; partNumber: number }>>({});

  /**
   * Reset the refs used to store data necessary for resuming an upload
   */
  const resetUploadRefs = useCallback(() => {
    cdsUrlRef.current = null;
    apiS3UrlRef.current = null;
    signedCdsUrlRef.current = null;
    partUrlsRef.current = null;
    uploadIdRef.current = null;
    itemIdRef.current = null;
    completedPartsRef.current = {};
  }, []);

  /**
   * Upload the parts of a file to the CDS.
   */
  const uploadParts = useCallback(
    async ({
      file,
      partUrls,
      bodyParts,
      uploadId,
      cdsUrl,
      signedCdsUrl,
      onProgress,
      onError,
      fileType,
      itemId,
    }: UploadPartsParams) => {
      const headers = {
        'Content-Type': file.type,

        // This header preserves the file's name when it is downloaded
        'Content-Disposition': `attachment; filename="${encodeURIComponent(file.name)}"`,
      };

      // The initial progress is calculated by the number of parts that have been uploaded (i.e number of
      // undefined elements in `bodyParts`) divided by the total number of parts `bodayParts.length`
      let progress = (Object.keys(completedPartsRef.current).length / bodyParts.length) * 100;
      onProgress && onProgress(progress);

      let lastError: Error | undefined;
      let erroredPartNumber: number | undefined;

      try {
        const parts = await pMap(
          partUrls,
          async (url: string, index: number) => {
            // Search for whether this part has already been uploaded using its index. If so,
            // return the eTag and partNumber of the completed part.
            const completedPart = completedPartsRef.current[index];
            if (completedPart) {
              return { eTag: completedPart.eTag, partNumber: completedPart.partNumber };
            }

            let partUrl = url;
            const partNumber = index + 1;
            for (let i = 0; i < 10; i++) {
              try {
                const response = await axios.put<{
                  headers: { etag: string };
                }>(partUrl as string, bodyParts[index], {
                  headers,
                  maxBodyLength: Infinity,
                  maxContentLength: Infinity,
                });

                if (response.status < 200 || response.status > 299) {
                  throw new Error(
                    `Response for upload of partUrl ${partUrl} was not successful, status code ${response.status}, status text ${response.statusText}`,
                  );
                }

                const eTag = response.headers.etag;
                progress += (1 / partUrls.length) * 100;
                onProgress && onProgress(progress);
                completedPartsRef.current[index] = { eTag, partNumber };

                return { eTag, partNumber };
              } catch (e) {
                lastError = e;
                erroredPartNumber = partNumber;
                await logEvent(Event.CDSMultipartUploadPartFailureEvent, {
                  assignmentId,
                  workerId,
                  cdsUrl,
                  signedCdsUrl,
                  partUrls,
                  uploadId,
                  erroredPartUrl: partUrl,
                  erroredPartNumber,
                  errorMessage: e.message,
                  errorStack: e.stack,
                });

                await sleep(1000);

                const { partUrl: newPartUrl } = await fetchApi(
                  `/contentUnderstanding/retrieveCDSPartUrl`,
                  {
                    body: {
                      cdsUrl,
                      partNumber,
                      uploadId,
                      assignmentId,
                    },
                    maxRetries: 3,
                    method: 'POST',
                    parseAndHandle: true,
                    redirectOnUnauthorized: true,
                  },
                );

                partUrl = newPartUrl;
                continue;
              }
            }

            await logEvent(Event.CDSMultipartUploadFailureEvent, {
              assignmentId,
              workerId,
              cdsUrl,
              signedCdsUrl,
              partUrls,
              uploadId,
              erroredPartNumber,
              errorMessage: lastError?.message,
              errorStack: lastError?.stack,
            });
            throw new Error('Failed upload. Please retry.');
          },
          // If a part fails to upload, we want to continue uploading the other parts.
          // Only once all parts have been uploaded or errored should we return an error
          // and allow the user to retry the upload.
          { concurrency: 100, stopOnError: false },
        );

        await fetchApi(`/contentUnderstanding/completeCDSMultipartUpload`, {
          body: {
            uploadId,
            parts,
            assignmentId,
            fileType,
            itemId,
          },
          maxRetries: 3,
          method: 'POST',
          parseAndHandle: true,
          redirectOnUnauthorized: true,
        });
      } catch (e) {
        onError && onError('Failed upload. Please retry.');
        throw new Error('Failed upload. Please retry.');
      }
    },
    [assignmentId, workerId],
  );

  /**
   * Get the parts of a file to be uploaded to the CDS.
   */
  const getPartBlobsForFile = useCallback((file: File, numBlobs: number) => {
    const bodyParts: Blob[] = [];
    for (let index = 0; index < numBlobs; index++) {
      const start = index * CDS_CHUNK_SIZE;
      const end = (index + 1) * CDS_CHUNK_SIZE;
      bodyParts.push(file.slice(start, end));
    }
    return bodyParts;
  }, []);

  /**
   * Client-facing function to retry an upload that failed. Should only be used in the event
   * that the `upload` function throws an error.
   */
  const retryUpload = useCallback(
    async ({ file, onProgress, onComplete, onError, fileType }: UploadInput) => {
      if (
        !cdsUrlRef.current ||
        !apiS3UrlRef.current ||
        !signedCdsUrlRef.current ||
        !partUrlsRef.current ||
        !uploadIdRef.current ||
        !itemIdRef.current
      ) {
        onError && onError('Unable to resume upload');
        throw new Error('Unable to resume upload');
      }

      const bodyParts = getPartBlobsForFile(file, partUrlsRef.current.length);

      await uploadParts({
        file,
        partUrls: partUrlsRef.current,
        bodyParts,
        uploadId: uploadIdRef.current,
        cdsUrl: cdsUrlRef.current,
        signedCdsUrl: signedCdsUrlRef.current,
        onProgress,
        onError,
        fileType,
        itemId: itemIdRef.current,
      });

      onComplete && onComplete(cdsUrlRef.current, signedCdsUrlRef.current);
      return {
        cdsUrl: cdsUrlRef.current,
        s3Url: apiS3UrlRef.current,
        signedCdsUrl: signedCdsUrlRef.current,
      };
    },
    [uploadParts, getPartBlobsForFile],
  );

  /**
   * Execute a multipart upload for a file to the CDS.
   */
  const executeMultipartUpload = useCallback(
    async ({
      file,
      onProgress,
      onComplete,
      onError,
      fileType,
    }: UploadInput): Promise<{
      cdsUrl: string;
      s3Url: string;
      signedCdsUrl: string;
    }> => {
      resetUploadRefs();
      const { cdsUrl, apiS3Url, signedCdsUrl, partUrls, uploadId, itemId } = await fetchApi(
        `/contentUnderstanding/startCDSMultipartUpload`,
        {
          body: {
            assignmentId,
            sizeBytes: file.size,
            mimeType: file.type,
          },
          maxRetries: 3,
          method: 'POST',
          parseAndHandle: true,
          redirectOnUnauthorized: true,
        },
      );

      cdsUrlRef.current = cdsUrl;
      apiS3UrlRef.current = apiS3Url;
      signedCdsUrlRef.current = signedCdsUrl;
      partUrlsRef.current = partUrls;
      uploadIdRef.current = uploadId;
      itemIdRef.current = itemId;

      const bodyParts = getPartBlobsForFile(file, partUrls.length);

      await uploadParts({
        file,
        partUrls,
        bodyParts,
        uploadId,
        cdsUrl,
        signedCdsUrl,
        onProgress,
        onError,
        fileType,
        itemId,
      });

      onComplete && onComplete(cdsUrl, signedCdsUrl);
      return { cdsUrl, s3Url: apiS3Url, signedCdsUrl };
    },
    [assignmentId, resetUploadRefs, uploadParts, getPartBlobsForFile],
  );

  /**
   * Client-facing function to upload a file to the CDS.
   */
  const upload = useCallback(
    async ({
      file,
      onProgress,
      onComplete,
      onError,
      fileType,
    }: UploadInput): Promise<{
      cdsUrl: string;
      s3Url: string;
      signedCdsUrl: string;
    }> => {
      if (!file) {
        throw Error('Name and file must be provided to upload a file');
      }

      return executeMultipartUpload({
        file,
        onProgress,
        onComplete,
        onError,
        fileType,
      });
    },
    [executeMultipartUpload],
  );

  return {
    upload,
    retryUpload,
  };
};

export default useCDSMultipartUpload;
